import { useEffect, useRef, useState } from 'react'
import { ApiError, api, type IntentCompiled, type IntentRefused } from '../lib/api'

// The intent bar reads and proposes. It never commits, and the grammar behind
// it compiles only to the same filter struct the chips below produce, so it can
// never return a set the traditional filters could not. The compiled expression
// is shown before the rows because a wrong filter omits records, and an omitted
// record is invisible in a way that a wrong record is not.

type Props = {
  onCompiled: (result: IntentCompiled, phrase: string) => void
  onCleared: () => void
}

export function IntentBar({ onCompiled, onCleared }: Props) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [expression, setExpression] = useState<string | null>(null)
  const [refused, setRefused] = useState<IntentRefused | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // No shortcut. This sits inside the filter popover now, which is opened
    // by the control it belongs to.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') clear()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  async function run(phrase: string) {
    const trimmed = phrase.trim()
    if (!trimmed) return
    setBusy(true)
    setFailure(null)
    try {
      const res = await api.compileIntent(trimmed)
      if (res.compiled) {
        setExpression(res.expression)
        setRefused(null)
        onCompiled(res, trimmed)
      } else {
        setExpression(null)
        setRefused(res)
      }
    } catch (err) {
      setExpression(null)
      setRefused(null)
      setFailure(err instanceof ApiError ? err.message + ' ' + err.remedy : 'The phrase could not be compiled.')
    } finally {
      setBusy(false)
    }
  }

  function clear() {
    setText('')
    setExpression(null)
    setRefused(null)
    setFailure(null)
    onCleared()
    input.current?.focus()
  }

  return (
    <section className="intent" aria-labelledby="intent-label">
      <h2 id="intent-label" className="vh">
        Compile a phrase into a portfolio filter
      </h2>
      <form
        className="intent-form"
        onSubmit={(e) => {
          e.preventDefault()
          void run(text)
        }}
      >
        <label className="vh" htmlFor="intent-input">
          Describe the vendors you are looking for
        </label>
        <input
          id="intent-input"
          ref={input}
          type="search"
          value={text}
          placeholder="overdue vendors touching personal data"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') clear()
          }}
        />
        <button type="submit" className="primary" data-action="tprm.portfolio.a01" disabled={busy || !text.trim()}>
          {busy ? 'Compiling' : 'Compile'}
        </button>
        {(expression || refused || text) && (
          <button type="button" onClick={clear}>
            Clear
          </button>
        )}
      </form>

      {expression && (
        <div className="expr">
          <span className="expr-label">Compiled filter, read it before you trust the rows</span>
          <code>{expression}</code>
        </div>
      )}

      {refused && (
        <div className="intent-refused" role="status">
          <p>{refused.message}</p>
          <ul>
            {refused.examples.map((ex) => (
              <li key={ex}>
                <button
                  type="button"
                  onClick={() => {
                    setText(ex)
                    void run(ex)
                  }}
                >
                  {ex}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {failure && (
        <div className="intent-refused" role="alert">
          <p>{failure}</p>
        </div>
      )}
    </section>
  )
}
